import { makeStyles } from '@material-ui/styles'
import Alert from 'core/components/Alert'
import CodeMirror from 'core/components/codeMirror/CodeMirror'
import PicklistField from 'core/components/validatedForm/DropdownField'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import TextField from 'core/components/validatedForm/TextField'
import Column from 'core/containers/Column'
import Button from 'core/elements/button/Button'
import Divider from 'core/elements/Divider'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { requiredValidator, yamlValidator } from 'core/utils/fieldValidators'
import ControlPlaneCard from 'k8s/components/cluster/deployment/form-components/ControlPlaneCard'
import React, { useCallback } from 'react'
import { getFetchModuleRequestBody, getVariablesDetails } from '../helper'
import TagsPicklist from './TagsPicklist'
import { fetchTerraformModule } from '../../repositories/actions'
import useUpdateAction from 'core/hooks/useUpdateAction'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'

const templateValidations = [requiredValidator, yamlValidator]

export default function CreateTemplateTerraformStep({
  params,
  getParamsUpdater,
  missingVariableError,
}) {
  const classes = useStyles()

  const {
    update: updateModule,
    error: moduleError,
    reset: resetModule,
    updating,
  } = useUpdateAction(fetchTerraformModule)

  const fetchModule = useCallback(async () => {
    const body = getFetchModuleRequestBody(params)
    const { success, response } = await updateModule({ body })
    if (success) {
      const fileDataMissing = !response?.file_data
      const variablesDetails = response?.variables?.length
        ? getVariablesDetails(response?.variables)
        : []
      getParamsUpdater('isFileDataMissing')(fileDataMissing)
      getParamsUpdater('templateYaml')(response?.file_data || '')
      getParamsUpdater('variables')(variablesDetails)
      getParamsUpdater('isModuleFetched')(true)
    } else {
      getParamsUpdater('isModuleFetched')(false)
      getParamsUpdater('isFileDataMissing')(false)
    }
  }, [params])

  const onButtonClick = (event) => {
    event?.preventDefault()
    fetchModule()
  }
  return (
    <>
      <Column gap={32}>
        <div className={classes.terraformTypeContainer}>
          <ControlPlaneCard
            id="managed"
            onClick={() => getParamsUpdater('creationStrategyType')('managed')}
            active={params?.creationStrategyType === 'managed'}
            title="Specify Terraform Module"
            description=""
          />
          <ControlPlaneCard
            id="custom"
            // onClick={() => getParamsUpdater('creationStrategyType')('custom')}
            onClick={() => {}}
            active={params?.creationStrategyType === 'custom'}
            title="Upload Terraform File"
            description=""
            // info="If you request more resources than what's provided in the template, your app will need to be approved by an admin."
            comingSoon
          />
        </div>
        {/*TODO:: Need to handle this case based on API response */}
        {/* {moduleError && (
          <div className={classes.formFieldSection}>
            <Alert
              title={
                params.terraformModulePath
                  ? 'Unable to fetch the Terraform Module'
                  : 'Invalid SSH Key'
              }
              variant="error"
            >
              <Text variant="body2">
                {params.terraformModulePath
                  ? `Please connect a Repository and add your SSH key under Orchestration > Connect
                Repository to enable access.`
                  : `To continue, please update your key under Orchestration > Connect Repository with a valid SSH key.`}
              </Text>
            </Alert>
          </div>
        )} */}

        {moduleError && (
          <div className={classes.formFieldSection}>
            <Alert
              title={
                moduleError?.raw?.response?.data?.detail || 'Unable to fetch the Terraform Module'
              }
              variant="error"
            >
              <Text variant="body2">
                Please connect a Repository and add your SSH key under Orchestration &gt; Connect
                Repository to enable access or please update your key under Orchestration &gt;
                Connect Repository with a valid SSH key.
              </Text>
            </Alert>
          </div>
        )}

        {params?.isFileDataMissing && (
          <div className={classes.formFieldSection}>
            <Alert title={'Unable to fetch the main.tf file'} variant="warning">
              <Text variant="body2">
                The main.tf file is missing from the specified Terraform module. While the app will
                still be deployed, the script content will not be displayed in the UI. Please make
                sure you are using a valid Terraform module to avoid unexpected issues.
              </Text>
            </Alert>
          </div>
        )}

        <div className={classes.subContainer}>
          <TextField
            id="terraformModulePath"
            label="Terraform Module Path"
            onChange={(value) => {
              getParamsUpdater('terraformModulePath')(value)
              getParamsUpdater('isModuleFetched')(false)
              getParamsUpdater('isFileDataMissing')(false)
            }}
            value={params?.terraformModulePath}
            info="Provide the path to the specific module in the repository you want to use."
          />
          <PicklistField
            id="tag"
            DropdownComponent={TagsPicklist}
            value={params?.tag}
            onChange={(value) => {
              getParamsUpdater('tag')(value)
              getParamsUpdater('isModuleFetched')(false)
              getParamsUpdater('isFileDataMissing')(false)
            }}
            placeholder="Select Branch"
            required
            isRequired
          />
        </div>
        <div className={classes.customButton}>
          <Button
            loading={updating}
            variant="secondary"
            disabled={params?.tag === ''}
            onClick={onButtonClick}
          >
            Fetch Your Terraform Module
          </Button>
        </div>

        {params?.isModuleFetched && (
          <>
            <Divider className={classes.divider} />
            <div className={classes.keyValueContainer}>
              <Text variant="caption1">Terraform Variables</Text>
              <Text variant="body2" className={classes.subTextContainer}>
                Define variables to customize your Terraform deployment. These variables will be
                passed to the script during provisioning.
              </Text>

              <KeyValuesField
                id="variables"
                label=""
                value={params?.variables}
                onChange={getParamsUpdater('variables')}
                addLabel="Add Variable"
                allowMultipleValues
                allowSecretValues
                fullWidth={true}
                data-testid="terraform-variables-key-values"
              />
              {missingVariableError && (
                <Text variant="body2" className={classes.error}>
                  <FontAwesomeIcon className={classes.icon}>exclamation-circle</FontAwesomeIcon>
                  All variables must have a key and value.
                </Text>
              )}
            </div>
            <div>
              <CodeMirror
                variant="light"
                id="templateYaml"
                loading={updating}
                // validations={templateValidations}
                label="Template YAML (main.tf)"
                onChange={getParamsUpdater('templateYaml')}
                value={params?.templateYaml}
                options={{ readOnly: true }}
                autoCursor={false}
              />
            </div>
          </>
        )}
      </Column>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  divider: {
    margin: theme.spacing(0.5, 0),
  },
  subContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
    // paddingLeft: 8,
    gap: 16,
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    background: colors.grey[50],
    padding: '12px 16px 16px 12px',
    borderRadius: theme.spacing(0.5),
    position: 'relative',
    border: `1px solid #E6E6EA`,
    gap: 8,
  },
  keyValueContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    alignItems: 'center',
    gap: 8,
  },
  customButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formFieldSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    alignItems: 'center',
  },
  terraformTypeContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'center',
    height: 60,
    gap: 16,
  },
  error: {
    marginLeft: 4,
    color: theme.components.input.error,
    lineHeight: '16px',
  },
  icon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
}))
